// Report grids served by the search API (REG-885).
//
// The Lead Explorer grid (rpt_lead_explorer_v, about 3.8M leads) filters,
// sorts, counts and pages through Typesense: POST /grid/leads takes the same
// filter model and ORDER BY terms the grid sends PostgREST, and answers with
// the page's view rows (read from the view as the user) and `found`, the
// number of matching leads. When the API answers `fallback` (live_source is
// postgres, Typesense is down or slow, or the filter or sort is one Typesense
// does not model) or cannot be reached, the grid reads PostgREST as before;
// after a failure it skips the API for a minute.
import { searchApiUrl } from './smartSearch';
import { supabaseClient } from './supabase';

export interface SearchGridOrder {
  column: string;
  ascending: boolean;
}

export interface SearchGridRequest {
  filterModel: Record<string, unknown>;
  order: SearchGridOrder[];
  startRow: number;
  endRow: number;
}

export interface SearchGridPage {
  rows: Record<string, any>[];
  found: number;
}

export type SearchGridFetcher = (request: SearchGridRequest) => Promise<SearchGridPage | null>;

/** Report key -> search API path. */
const SEARCH_API_GRIDS: Record<string, string> = {
  lead_explorer: '/grid/leads',
};

const GRID_TIMEOUT_MS = 6000;
const GRID_RETRY_MS = 60_000;
let gridDownUntil = 0;

/** The search API fetcher for a report's grid, or null when Postgres serves it. */
export function searchGridFetcher(reportKey: string, base: string | undefined = searchApiUrl): SearchGridFetcher | null {
  const path = SEARCH_API_GRIDS[reportKey];
  if (!base || !path) return null;
  return async (request) => {
    if (Date.now() < gridDownUntil) return null;
    const { data: { session } } = await supabaseClient.auth.getSession();
    if (!session) return null;
    try {
      const response = await fetch(`${base}${path}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${session.access_token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(request),
        signal: AbortSignal.timeout(GRID_TIMEOUT_MS),
      });
      if (!response.ok) throw new Error(`search API ${response.status}`);
      const body = (await response.json()) as Partial<SearchGridPage> & { fallback?: string };
      if (body.fallback || !Array.isArray(body.rows) || typeof body.found !== 'number') return null;
      return { rows: body.rows, found: body.found };
    } catch (error) {
      gridDownUntil = Date.now() + GRID_RETRY_MS;
      console.warn('report grid: search API unavailable, using Postgres', error);
      return null;
    }
  };
}
